import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CreditCard, CheckCircle, XCircle, Clock, User, Calendar, Wallet, DollarSign, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { useTranslation } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceInterface = () => {
  const { token } = useAuth();
  const { t, language } = useTranslation();
  const [cardId, setCardId] = useState("");
  const [cardResult, setCardResult] = useState(null);
  const [mealSelection, setMealSelection] = useState("table_dhote");
  const [customAmount, setCustomAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  const [deductionResult, setDeductionResult] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const inputRef = useRef(null);
  const cardResultRef = useRef(null); // Add ref for card result section

  const axiosConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };

  useEffect(() => {
    // Focus input on component mount
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };
    
    focusInput();
    
    // Don't add page click listener that interferes with scroll
    // Let user manually focus when ready for next card
  }, []);

  const handleCardScan = async (e) => {
    e.preventDefault();
    if (!cardId.trim()) return;

    setProcessing(true);
    setDeductionResult(null);
    
    try {
      const response = await axios.post(`${API}/check-card/${cardId.trim()}`);
      setCardResult(response.data);
      setLastScanned(new Date());

      if (response.data.success) {
        toast.success(t("service.cardFound", { student: response.data.student_name }));
      } else {
        toast.error(response.data.message);
      }

      // Scroll to card result and STAY there - no auto-focus to interfere
      setTimeout(() => {
        if (cardResultRef.current) {
          const elementTop = cardResultRef.current.offsetTop - 120; // More offset for better view
          window.scrollTo({
            top: elementTop,
            behavior: 'smooth'
          });
        }
      }, 500); // Longer delay to ensure DOM is fully rendered
      
    } catch (error) {
      console.error("Error checking card:", error);
      setCardResult({
        success: false,
        message: t("service.systemError"),
        student_name: null
      });
      toast.error(t("service.systemError"));
      
      // Scroll to show the error and STAY there
      setTimeout(() => {
        if (cardResultRef.current) {
          const elementTop = cardResultRef.current.offsetTop - 120;
          window.scrollTo({
            top: elementTop,
            behavior: 'smooth'
          });
        }
      }, 500);
    } finally {
      setProcessing(false);
      // DO NOT auto-focus the input - let user see the result
      // They can manually click/tap input when ready for next card
    }
  };

  const handleKeyDown = (e) => {
    // Auto-submit on Enter (common for RFID readers)
    if (e.key === 'Enter' && cardId.trim()) {
      handleCardScan(e);
    }
  };

  const handleMealDeduction = async () => {
    if (!cardResult || !cardResult.success) {
      toast.error(t("service.scanPrompt"));
      return;
    }

    if (mealSelection === 'custom' && (!customAmount || parseFloat(customAmount) <= 0)) {
      toast.error(t("service.customAmountPrompt"));
      return;
    }

    setProcessing(true);
    
    try {
      const deductionData = {
        card_id: cardId.trim(),
        meal_type: mealSelection,
        ...(mealSelection === 'custom' && { custom_amount: parseFloat(customAmount) })
      };

      const response = await axios.post(`${API}/deduct-meal`, deductionData, axiosConfig);
      setDeductionResult(response.data);
      
      // Update card result with new balance
      setCardResult(prev => ({
        ...prev,
        current_balance: response.data.balance_after
      }));

      // Show modal instead of toast
      setShowResultModal(true);
    } catch (error) {
      console.error("Error deducting meal:", error);
      const errorMessage = error.response?.data?.detail || t("service.deductionError");
      setDeductionResult({
        success: false,
        message: errorMessage,
        student_name: cardResult?.student_name
      });
      setShowResultModal(true);
    } finally {
      setProcessing(false);
    }
  };

  const closeResultModal = () => {
    setShowResultModal(false);
    setDeductionResult(null);
    
    // Clear everything for next customer
    setCardResult(null);
    setCardId("");
    setMealSelection("table_dhote");
    setCustomAmount("");
    setLastScanned(null);
    
    // Focus the input field after modal closes and scroll to top
    setTimeout(() => {
      // Scroll to top first
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      
      // Then focus input after scroll completes
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select(); // Select any existing text
        }
      }, 500);
    }, 100);
  };

  const clearResult = () => {
    setCardResult(null);
    setDeductionResult(null);
    setCardId("");
    setMealSelection("table_dhote");
    setCustomAmount("");
    setLastScanned(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', { 
      style: 'currency', 
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  const getMealPrice = (mealType) => {
    const prices = {
      table_dhote: 100,
      alacarte: 150
    };
    return prices[mealType] || 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("service.title")}</h1>
          <p className="text-lg text-gray-600">{t("service.subtitle")}</p>
          {lastScanned && (
            <p className="text-sm text-gray-500 mt-2">
              {t("service.lastScanned", { time: lastScanned.toLocaleTimeString(language === 'tr' ? 'tr-TR' : undefined) })}
            </p>
          )}
        </div>

        {/* Card Input Section */}
        <Card className="mb-8 shadow-lg">
          <CardContent className="p-8">
            <form onSubmit={handleCardScan} className="space-y-6">
              <div className="text-center">
                <CreditCard className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                <label htmlFor="cardId" className="block text-lg font-medium text-gray-900 mb-4">
                  {t("service.cardScannerLabel")}
                </label>
                <Input
                  ref={inputRef}
                  id="cardId"
                  type="text"
                  value={cardId}
                  onChange={(e) => setCardId(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("service.cardPlaceholder")}
                  className="text-center text-xl py-4 border-2 border-gray-300 focus:border-blue-500 transition-colors"
                  disabled={processing}
                />
              </div>
              <div className="flex justify-center space-x-4">
                <Button
                  type="submit"
                  disabled={!cardId.trim() || processing}
                  className="bg-blue-600 hover:bg-blue-700 px-8 py-3 text-lg"
                >
                  {processing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t("service.checking")}
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      {t("service.checkCard")}
                    </>
                  )}
                </Button>
                {(cardResult || deductionResult) && (
                  <Button
                    type="button"
                    onClick={clearResult}
                    variant="outline"
                    className="px-8 py-3 text-lg"
                  >
                    {t("service.clear")}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Card Result Display */}
        {cardResult && (
          <Card 
            ref={cardResultRef}
            className={`shadow-lg mb-8 transition-all duration-300 ${
              cardResult.success 
                ? 'border-blue-200 bg-blue-50' 
                : 'border-red-200 bg-red-50'
            }`}
          >
            <CardContent className="p-8">
              <div className="text-center">
                {cardResult.success ? (
                  <Wallet className="h-20 w-20 text-blue-600 mx-auto mb-4" />
                ) : (
                  <XCircle className="h-20 w-20 text-red-600 mx-auto mb-4" />
                )}
                
                <h2 className={`text-3xl font-bold mb-4 ${
                  cardResult.success ? 'text-blue-800' : 'text-red-800'
                }`}>
                  {cardResult.success ? t("service.cardStatus.found") : t("service.cardStatus.issue")}
                </h2>

                <p className={`text-xl mb-6 ${
                  cardResult.success ? 'text-blue-700' : 'text-red-700'
                }`}>
                  {cardResult.message}
                </p>

                {cardResult.student_name && (
                  <div className="bg-white rounded-lg p-6 shadow-inner max-w-lg mx-auto mb-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-center space-x-2">
                        <User className="h-5 w-5 text-gray-600" />
                        <span className="text-lg font-semibold text-gray-900">
                          {cardResult.student_name}
                        </span>
                      </div>
                      
                        <div className="flex items-center justify-center space-x-2">
                          <Wallet className="h-5 w-5 text-gray-600" />
                          <span className={`text-xl font-bold ${
                            cardResult.current_balance > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                          {t("service.balance", { amount: formatPrice(cardResult.current_balance) })}
                          </span>
                        </div>

                        {cardResult.subscription_expires && (
                          <div className="flex items-center justify-center space-x-2">
                            <Calendar className="h-5 w-5 text-gray-600" />
                          <span className="text-gray-700">
                            {t("service.expires", { date: formatDate(cardResult.subscription_expires) })}
                          </span>
                            {new Date(cardResult.subscription_expires) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 ml-2">
                              {t("service.expiresSoon")}
                              </Badge>
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Meal Selection for successful cards */}
                {cardResult.success && cardResult.current_balance > 0 && (
                  <div className="bg-white rounded-lg p-6 shadow-inner max-w-lg mx-auto">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("service.selectMeal")}</h3>
                    <div className="space-y-4">
                      {/* Meal Type Buttons */}
                      <div className="grid grid-cols-1 gap-3">
                        <Button
                          onClick={() => setMealSelection('table_dhote')}
                          variant={mealSelection === 'table_dhote' ? 'default' : 'outline'}
                          className={`p-4 text-left justify-between ${
                            mealSelection === 'table_dhote' 
                              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                              : 'hover:bg-blue-50 border-blue-200'
                          }`}
                        >
                          <span>{t("service.meals.table_dhote")}</span>
                          <span className="font-bold">{formatPrice(getMealPrice('table_dhote'))}</span>
                        </Button>

                        <Button
                          onClick={() => setMealSelection('alacarte')}
                          variant={mealSelection === 'alacarte' ? 'default' : 'outline'}
                          className={`p-4 text-left justify-between ${
                            mealSelection === 'alacarte' 
                              ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                              : 'hover:bg-purple-50 border-purple-200'
                          }`}
                        >
                          <span>{t("service.meals.alacarte")}</span>
                          <span className="font-bold">{formatPrice(getMealPrice('alacarte'))}</span>
                        </Button>

                        <Button
                          onClick={() => setMealSelection('custom')}
                          variant={mealSelection === 'custom' ? 'default' : 'outline'}
                          className={`p-4 text-left justify-between ${
                            mealSelection === 'custom' 
                              ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                              : 'hover:bg-orange-50 border-orange-200'
                          }`}
                        >
                          <span>{t("service.meals.custom")}</span>
                          <span className="font-bold">{t("service.meals.customLabel")}</span>
                        </Button>
                      </div>

                      {mealSelection === 'custom' && (
                        <div className="mt-3">
                          <Input
                            type="number"
                            step="0.01"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            placeholder={t("service.customPlaceholder")}
                            className="text-center text-lg py-3"
                            autoFocus
                          />
                        </div>
                      )}

                      <Button
                        onClick={handleMealDeduction}
                        disabled={processing || (mealSelection === 'custom' && !customAmount)}
                        className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg mt-4"
                      >
                        {processing ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            {t("service.processing")}
                          </>
                        ) : (
                          <>
                            <DollarSign className="h-5 w-5 mr-2" />
                            {t("service.deduct", {
                              amount: mealSelection === 'custom'
                                ? formatPrice(parseFloat(customAmount) || 0)
                                : formatPrice(getMealPrice(mealSelection))
                            })}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Result Modal */}
        <Dialog open={showResultModal} onOpenChange={setShowResultModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{t("service.paymentResult")}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeResultModal}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogTitle>
            </DialogHeader>
            
            {deductionResult && (
              <div className="text-center py-6">
                {deductionResult.success ? (
                  <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
                )}
                
                <h3 className={`text-2xl font-bold mb-3 ${
                  deductionResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {deductionResult.success ? t("service.paymentSuccessful") : t("service.paymentFailed")}
                </h3>

                <p className={`text-lg mb-4 ${
                  deductionResult.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {deductionResult.message}
                </p>

                {deductionResult.success && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{t("service.paymentDetails.student")}</span>
                        <span className="font-semibold">{deductionResult.student_name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{t("service.paymentDetails.amountDeducted")}</span>
                        <span className="font-bold text-red-600">-{formatPrice(deductionResult.amount_deducted)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{t("service.paymentDetails.previousBalance")}</span>
                        <span className="font-semibold">{formatPrice(deductionResult.balance_before)}</span>
                      </div>
                      <div className="flex justify-between items-center border-t pt-2">
                        <span className="text-gray-600">{t("service.paymentDetails.newBalance")}</span>
                        <span className={`text-lg font-bold ${
                          deductionResult.balance_after > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatPrice(deductionResult.balance_after)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={closeResultModal}
                  className={`w-full py-3 ${
                    deductionResult.success
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {deductionResult.success ? t("service.nextCustomer") : t("service.tryAgain")}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Instructions */}
        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">{t("service.instructionsTitle")}</h3>
            <ul className="text-blue-800 space-y-2">
              {t("service.instructions").map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg p-4 text-center shadow">
            <div className="text-2xl font-bold text-green-600">✓</div>
            <div className="text-sm text-gray-600">{t("service.quickStats.success")}</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow">
            <div className="text-2xl font-bold text-red-600">✗</div>
            <div className="text-sm text-gray-600">{t("service.quickStats.failed")}</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow">
            <div className="text-2xl font-bold text-blue-600">₺100</div>
            <div className="text-sm text-gray-600">{t("service.quickStats.table")}</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow">
            <div className="text-2xl font-bold text-purple-600">₺150</div>
            <div className="text-sm text-gray-600">{t("service.quickStats.alacarte")}</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center shadow">
            <div className="text-2xl font-bold text-orange-600">∞</div>
            <div className="text-sm text-gray-600">{t("service.quickStats.unlimited")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceInterface;